import dayjs from 'dayjs';
import { isInvoiceQuote } from './invoice-doc-type';

/** Mirrors {@see QuoteExpiryService::DEFAULT_DAYS_AFTER_SEND} in PHP. */
export const QUOTE_EXPIRY_DEFAULT_DAYS_AFTER_SEND = 14;

export type ResolveQuoteExpiryInput = {
    expiryDate?: string | number | null;
    status?: string | null;
    invoiceSentDate?: string | number | null;
    quoteDecisionDate?: string | null;
    /** New-business pipeline: server ExpiryDate is null until first send. */
    expiryAutoSetOnSend?: boolean;
    /** Enables the editor-only indicative expiry for an unsent draft. */
    allowProjection?: boolean;
    referenceDate?: Date;
};

export type ResolvedQuoteExpiry = {
    /** Calendar date (`YYYY-MM-DD`) for display, or null when not a quote. */
    displayDate: string | null;
    /** True when the date is projected from today, not persisted on the invoice. */
    isProjected: boolean;
};

function toDateOnly(value: string | number | null | undefined): string | null {
    if (value == null || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return date.toISOString().slice(0, 10);
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch?.[1]) {
        return isoMatch[1];
    }

    const parsed = dayjs(trimmed);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function hasInvoiceSentDate(
    value: string | number | null | undefined,
): boolean {
    return toDateOnly(value) != null;
}

/**
 * Resolves the quote expiry date shown in BM and on the proposal page.
 *
 * When ExpiryDate is empty on an unsent draft with {@see expiryAutoSetOnSend},
 * the editor may project today + 14 days (same preview as QuoteExpiryService for
 * the new-business pipeline). Customer-facing and sent quotes never receive a
 * render-time fallback expiry.
 */
export function resolveQuoteExpiry(
    input: ResolveQuoteExpiryInput,
): ResolvedQuoteExpiry {
    const concrete = toDateOnly(input.expiryDate);
    if (concrete) {
        return { displayDate: concrete, isProjected: false };
    }

    if (!isInvoiceQuote(input.status, input.quoteDecisionDate)) {
        return { displayDate: null, isProjected: false };
    }

    if (!input.allowProjection || !input.expiryAutoSetOnSend) {
        return { displayDate: null, isProjected: false };
    }

    const isUnsentDraft =
        input.status === 'Draft' && !hasInvoiceSentDate(input.invoiceSentDate);
    if (!isUnsentDraft) {
        return { displayDate: null, isProjected: false };
    }

    const referenceDate = input.referenceDate ?? new Date();
    const projected = dayjs(referenceDate)
        .add(QUOTE_EXPIRY_DEFAULT_DAYS_AFTER_SEND, 'day')
        .format('YYYY-MM-DD');

    return {
        displayDate: projected,
        isProjected: true,
    };
}
